import $ from '../core/Dom';

/**
 * Hero code card.
 *
 * Rotates through the code snippets, and lets the tabs jump straight to one.
 * Rotation pauses while the pointer is over the card, or while a tab has focus.
 *
 * All panels are in the markup already — this only toggles which one is shown,
 * so the card still renders the first snippet if the JS never boots.
 */
export default (el, props = {}) => {
    const $el = $(el);
    const interval = (props || {}).interval || 6000;

    let tabs = [];
    let panels = [];
    let fileLabel = null;
    let kindLabel = null;

    let index = 0;
    let paused = false;
    let stopped = false;
    let timer = null;

    const show = next => {
        index = ((next % panels.length) + panels.length) % panels.length;

        panels.forEach((panel, i) => {
            $(panel).toggleClass('is-hidden', i !== index);
        });

        tabs.forEach((tab, i) => {
            const isActive = i === index;
            $(tab).toggleClass('is-active', isActive);
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        const activeTab = tabs[index];

        if (activeTab && fileLabel) {
            fileLabel.textContent = activeTab.getAttribute('data-file') || '';
        }

        if (activeTab && kindLabel) {
            kindLabel.textContent = activeTab.getAttribute('data-kind') || '';
        }
    };

    const stop = () => {
        if (timer) {
            clearInterval(timer);
            timer = null;
        }
    };

    const start = () => {
        stop();

        // A single snippet has nothing to rotate through
        if (panels.length < 2) {
            return;
        }

        // Someone has picked a snippet; leave it alone
        if (stopped) {
            return;
        }

        timer = setInterval(() => {
            if (paused) {
                return;
            }
            show(index + 1);
        }, interval);
    };

    const onTabClick = e => {
        // Dom.js delegation puts the matched element on `triggerTarget`
        const tab = e.triggerTarget;

        // Rotating on is fine while nobody's asked for anything, but once a
        // tab is picked, moving off it a few seconds later is just rude
        stopped = true;
        stop();

        show(parseInt(tab.getAttribute('data-code-card-tab'), 10) || 0);
    };

    const onPointerEnter = () => {
        paused = true;
    };

    const onPointerLeave = () => {
        paused = false;
    };

    const init = () => {
        tabs = $el.find('[data-code-card-tab]').get();
        panels = $el.find('[data-code-card-panel]').get();
        fileLabel = $el.find('[data-code-card-file]').get(0);
        kindLabel = $el.find('[data-code-card-kind]').get(0);

        if (!panels.length) {
            return;
        }

        $el.on('click', '[data-code-card-tab]', onTabClick);

        // Hovering or tabbing into the card holds the current snippet
        $el.on('pointerenter', onPointerEnter);
        $el.on('pointerleave', onPointerLeave);
        $el.on('focusin', onPointerEnter);
        $el.on('focusout', onPointerLeave);

        show(0);
        start();
    };

    const destroy = () => {
        stop();
        $el.off('click');
        $el.off('pointerenter');
        $el.off('pointerleave');
        $el.off('focusin');
        $el.off('focusout');
        stopped = false;
        tabs = [];
        panels = [];
    };

    return {
        init,
        destroy
    };
};
